<template>
    <b-container fluid class="eval-profile-management py-4">
        <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
                <h4 class="mb-1">AI 평가 프로파일</h4>
                <p class="text-muted mb-0">
                    질문지별로 평가자의 평가 기준을 등록해 AI 평가에 적용합니다.
                    작성하지 않은 항목은 시스템 기본 기준이 적용됩니다.
                </p>
            </div>
            <b-button variant="primary" @click="openCreateModal">
                + 새 프로파일
            </b-button>
        </div>

        <b-card no-body class="p-3">
            <div class="d-flex align-items-center mb-3">
                <b-form-select
                    v-model="statusFilter"
                    :options="statusFilterOptions"
                    style="max-width: 200px"
                    @change="fetchProfiles"
                />
                <span v-if="!loading" class="ml-3 text-muted">총 {{ profiles.length }}개</span>
            </div>

            <b-table
                :items="profiles"
                :fields="fields"
                :busy="loading"
                hover
                responsive
                show-empty
                empty-text="등록된 평가 프로파일이 없습니다."
            >
                <template #table-busy>
                    <div class="text-center text-muted my-3">
                        <b-spinner small class="align-middle mr-2" />
                        <span class="align-middle">불러오는 중...</span>
                    </div>
                </template>

                <template #cell(status)="row">
                    <b-badge :variant="statusVariant(row.item.status)">
                        {{ statusLabel(row.item.status) }}
                    </b-badge>
                </template>

                <template #cell(linked_projects)="row">
                    <template v-if="row.item.linked_projects && row.item.linked_projects.length">
                        <b-badge
                            v-for="lp in row.item.linked_projects"
                            :key="lp.project_code"
                            variant="info"
                            class="mr-1 mb-1"
                        >
                            {{ lp.label || lp.project_code }}
                        </b-badge>
                    </template>
                    <span v-else class="text-muted">-</span>
                </template>

                <template #cell(updated_at)="row">
                    {{ formatDate(row.item.updated_at || row.item.created_at) }}
                </template>

                <template #cell(actions)="row">
                    <b-button size="sm" variant="outline-secondary" class="mr-1"
                              @click="openEditForm(row.item)">
                        편집
                    </b-button>
                    <b-button size="sm" variant="outline-info" class="mr-1"
                              @click="openCompare(row.item)">
                        비교 실행
                    </b-button>
                    <b-dropdown size="sm" variant="outline-secondary" text="..." right no-caret>
                        <b-dropdown-item v-if="row.item.status === 'draft'"
                                         @click="changeStatus(row.item, 'active')">
                            운영 상태로 적용
                        </b-dropdown-item>
                        <b-dropdown-item v-if="row.item.status === 'active'"
                                         @click="changeStatus(row.item, 'archived')">
                            보관 처리
                        </b-dropdown-item>
                        <b-dropdown-item v-if="row.item.status === 'archived'"
                                         @click="changeStatus(row.item, 'draft')">
                            초안으로 복원
                        </b-dropdown-item>
                        <b-dropdown-divider />
                        <b-dropdown-item variant="danger" @click="confirmDelete(row.item)">
                            삭제
                        </b-dropdown-item>
                    </b-dropdown>
                </template>
            </b-table>
        </b-card>

        <!-- 생성 모달 -->
        <b-modal id="create-profile-modal" title="새 평가 프로파일" ok-title="생성"
                 cancel-title="취소" @ok="createProfile">
            <b-form-group label="프로파일 이름" label-for="new-profile-name">
                <b-form-input id="new-profile-name" v-model="newProfileName"
                              placeholder="예: 공급망 평가 기준 v2" autofocus />
            </b-form-group>
            <p class="text-muted small mb-0">
                생성 후 편집 화면에서 질문에 답하면 평가 기준이 만들어집니다.
            </p>
        </b-modal>

        <!-- 작성 폼 (편집) -->
        <EvalProfileForm
            v-if="editingProfileId"
            :profile-id="editingProfileId"
            @saved="onFormSaved"
            @close="editingProfileId = null"
        />

        <!-- 비교 실행 -->
        <EvalProfileCompare
            v-if="compareProfile"
            :base-profile="compareProfile"
            :profiles="profiles"
            @close="compareProfile = null"
        />
    </b-container>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import EvalProfileForm from './EvalProfileForm.vue';
import EvalProfileCompare from './EvalProfileCompare.vue';

export default {
    name: 'EvalProfileManagement',
    components: { EvalProfileForm, EvalProfileCompare },
    data() {
        return {
            profiles: [],
            loading: false,
            statusFilter: '',
            newProfileName: '',
            editingProfileId: null,
            compareProfile: null,
            statusFilterOptions: [
                { value: '', text: '전체 상태' },
                { value: 'draft', text: '초안 (draft)' },
                { value: 'active', text: '운영 (active)' },
                { value: 'archived', text: '보관 (archived)' },
            ],
            fields: [
                { key: 'profile_name', label: '프로파일명' },
                { key: 'status', label: '상태' },
                { key: 'linked_projects', label: '연결 프로젝트' },
                { key: 'version', label: '버전' },
                { key: 'updated_at', label: '최종 수정' },
                { key: 'actions', label: '관리' },
            ],
        };
    },
    created() {
        this.fetchProfiles();
    },
    methods: {
        async fetchProfiles() {
            this.loading = true;
            try {
                const params = this.statusFilter ? { status: this.statusFilter } : {};
                const { data } = await this.$axios.get('/eval-profiles', { params });
                this.profiles = data;
            } catch (e) {
                this.$bvToast.toast('프로파일 목록을 불러오지 못했습니다.', {
                    title: '오류', variant: 'danger',
                });
            } finally {
                this.loading = false;
            }
        },
        openCreateModal() {
            this.newProfileName = '';
            this.$bvModal.show('create-profile-modal');
        },
        async createProfile(evt) {
            const name = (this.newProfileName || '').trim();
            if (!name) {
                evt.preventDefault();
                return;
            }
            try {
                const { data } = await this.$axios.post('/eval-profiles', {
                    profile_name: name,
                });
                await this.fetchProfiles();
                // 생성 직후 바로 편집 폼 오픈
                this.editingProfileId = data.id;
            } catch (e) {
                this.$bvToast.toast('프로파일 생성에 실패했습니다.', {
                    title: '오류', variant: 'danger',
                });
            }
        },
        openEditForm(profile) {
            this.editingProfileId = profile.id;
        },
        openCompare(profile) {
            this.compareProfile = profile;
        },
        onFormSaved() {
            // 저장 후 편집 모달을 닫고 목록을 갱신한다.
            this.editingProfileId = null;
            this.fetchProfiles();
        },
        async changeStatus(profile, newStatus) {
            try {
                await this.$axios.patch(`/eval-profiles/${profile.id}`, { status: newStatus });
                await this.fetchProfiles();
            } catch (e) {
                const msg = getErrorMessage(e, '상태 변경에 실패했습니다.');
                this.$bvToast.toast(msg, { title: '오류', variant: 'danger' });
            }
        },
        async confirmDelete(profile) {
            const ok = await this.$bvModal.msgBoxConfirm(
                `'${profile.profile_name}' 프로파일을 삭제하시겠습니까? 답변과 질문지 연결이 함께 삭제됩니다.`,
                { title: '삭제 확인', okVariant: 'danger', okTitle: '삭제', cancelTitle: '취소' },
            );
            if (!ok) return;
            try {
                await this.$axios.delete(`/eval-profiles/${profile.id}`);
                await this.fetchProfiles();
            } catch (e) {
                this.$bvToast.toast('삭제에 실패했습니다.', { title: '오류', variant: 'danger' });
            }
        },
        statusLabel(status) {
            return { draft: '초안', active: '운영', archived: '보관' }[status] || status;
        },
        statusVariant(status) {
            return { draft: 'secondary', active: 'success', archived: 'light' }[status] || 'secondary';
        },
        formatDate(iso) {
            if (!iso) return '-';
            return String(iso).replace('T', ' ').slice(0, 16);
        },
    },
};
</script>

<style scoped>
.eval-profile-management h4 {
    font-weight: 700;
}
</style>
